import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isEmpty, size } from 'lodash'
import { defaultTableConfigs } from 'pages/Administration/components/Table/configs'
import { workingTableColumn } from './config'

import Table from 'pages/Administration/components/Table'
import DocumentTypesPanel from './DocumentTypesPanel'
import {
  getRowNodeId,
  handleColumnDefs,
  onExportData,
  sendNotification,
  excelStyles,
  sizeTableToFit
} from 'pages/Administration/helpers'
import {
  defaultActions,
  frameworkComponents
} from 'pages/Administration/constants'
import {
  handleOpenModal,
  getPermissionPayload,
  updatePermissionModal,
  resetPermissionModal
} from 'pages/Administration/Tabs/Instances/helpers'
import { createWorkingTablesApi } from 'services/api/administration/workingTables'
import { useAgGridTranslation } from 'utils/hooks/useDocumentTranslation'

const WorkingTablesPanel = ({ admin, instanceType, instanceSelected }) => {
  const { workingTables } = admin
  const { t, i18n } = useTranslation(['Administration', 'Common'])

  const [gridApi, setGridApi] = useState(null)
  const [columnApi, setColumnApi] = useState(null)

  const [selectedRows, setSelectedRows] = useState([])
  const [updateSelected, setUpdateSelected] = useState({})

  const [cellHasBeenClicked, setCellHasBeenClicked] = useState(false)

  const {
    getWorkingTableByInstanceForRole,
    updateWTPermissions,
    updateWTPermissionsBulk
  } = useMemo(() => createWorkingTablesApi(instanceType), [instanceType])

  useEffect(async () => {
    setSelectedRows([])
    await getWorkingTableByInstanceForRole(instanceSelected)
  }, [instanceSelected])

  const onRowSelect = useCallback(
    (rowList) => {
      setSelectedRows(rowList)
    },
    [setSelectedRows]
  )

  const notifySuccess = (info) => sendNotification({ t, info, kind: 'success' })

  // UPDATE MODAL

  const onUpdateModalSubmit = async () => {
    const payload = getUpdatePayload(updateSelected)

    try {
      await updateWTPermissionsBulk(payload)
      await getWorkingTableByInstanceForRole({
        ubliqueId: updateSelected.instanceId,
        roleName: updateSelected.roleName
      })
      gridApi.deselectAll()
      setSelectedRows([])
      notifySuccess('UpdateAll')
    } catch (err) {
      console.error(err)
    }
  }

  const onUpdateSecondaryModalSubmit = async () => {
    const payload = getUpdatePayload(updateSelected)

    try {
      await updateWTPermissions(payload)
      await getWorkingTableByInstanceForRole({
        ubliqueId: updateSelected.instanceId,
        roleName: updateSelected.roleName
      })
      gridApi.deselectAll()
      setSelectedRows([])
      notifySuccess('Update')
    } catch (err) {
      console.error(err)
    }
  }

  // RESET MODAL

  const onResetModalSubmit = async () => {
    const app = selectedRows[0]
    const payload = getResetPayload(app)

    try {
      await updateWTPermissionsBulk(payload)
      await getWorkingTableByInstanceForRole({
        ubliqueId: app.instanceId,
        roleName: app.roleName
      })
      gridApi.deselectAll()
      setSelectedRows([])
      notifySuccess('ResetAll')
    } catch (err) {
      console.error(err)
    }
  }

  const onResetSecondaryModalSubmit = async () => {
    const app = selectedRows[0]
    const payload = getResetPayload(app)

    try {
      await updateWTPermissions(payload)
      await getWorkingTableByInstanceForRole({
        ubliqueId: app.instanceId,
        roleName: app.roleName
      })
      gridApi.deselectAll()
      setSelectedRows([])
      notifySuccess('Reset')
    } catch (err) {
      console.error(err)
    }
  }

  const handleResetAllPermissions = () => {
    resetPermissionModal(onResetModalSubmit, onResetSecondaryModalSubmit, t)
  }

  function getUpdatePayload(element) {
    const permissionPayload = getPermissionPayload(element)

    const payload = {
      workingTableId: element.idWT,
      instanceTitle: element.instanceTitle,
      instanceId: element.instanceId,
      type: element.type,
      roleName: element.roleName,
      permissions: [
        { permission: permissionPayload, roleName: element.roleName }
      ]
    }
    return payload
  }

  function getResetPayload(element) {
    const permissionPayload = {
      read: false,
      import_: false,
      export: false,
      create: false,
      update: false,
      delete: false
    }

    const payload = {
      workingTableId: element.ubliqueId,
      type: element.type,
      permissions: [
        { permission: permissionPayload, roleName: element.roleName }
      ]
    }
    return payload
  }

  const headerActions = useMemo(() => {
    if (!isEmpty(gridApi)) {
      return {
        selected: [
          {
            id: 'reset',
            title: t('Common:ResetPermissions'),
            handleClick: () => handleResetAllPermissions()
          }
        ],
        empty: [
          {
            id: 'exportRole',
            title: t('Administration:RolesManagement.Actions.ExportAll'),
            handleClick: () => {
              gridApi &&
                columnApi &&
                onExportData(
                  gridApi,
                  columnApi,
                  t('RolesManagement.WorkingTableTitle')
                )
            }
          }
        ]
      }
    }
    return defaultActions
  }, [t, gridApi, handleResetAllPermissions, onExportData])

  const { t: agGridT, destroyed } = useAgGridTranslation()

  useEffect(() => {
    if (cellHasBeenClicked) {
      updatePermissionModal(
        onUpdateModalSubmit,
        onUpdateSecondaryModalSubmit,
        t
      )
      setCellHasBeenClicked(false)
    }
  }, [cellHasBeenClicked])

  return (
    <div>
      {/*<h2 className='no-action-view'>*/}
      {/*  {t('RolesManagement.WorkingTableTitle')}*/}
      {/*</h2>*/}
      <Table
        headerActions={headerActions}
        columnDefs={handleColumnDefs(workingTableColumn, t)}
        rowData={size(workingTables) ? workingTables : []}
        getRowNodeId={getRowNodeId}
        excelStyles={excelStyles}
        key={i18n.language}
        onCellClicked={(e) => {
          setUpdateSelected({
            type: e.data.type,
            instanceTitle: e.data.instanceTitle,
            instanceId: e.data.instanceId,
            idWT: e.data.ubliqueId,
            roleName: e.data.roleName,
            wtTitle: e.data.title,
            column: e.colDef.colId,
            oldPermission: e.data.permission
          })
          if (handleOpenModal(e)) {
            setCellHasBeenClicked(true)
          }
        }}
        onGridReady={({ api, columnApi }) => {
          setGridApi(api)
          setColumnApi(columnApi)
          sizeTableToFit(api)
        }}
        onSelectionChanged={onRowSelect}
        frameworkComponents={frameworkComponents}
        defaultTableConfigs={defaultTableConfigs}
        localeTextFunc={agGridT}
      />

      {selectedRows.length === 1 ? (
        <div className='role-view' key={selectedRows[0]}>
          <div className='children-view no-margin'>
            <DocumentTypesPanel
              admin={admin}
              instanceType={instanceType}
              workingTableSelected={selectedRows}
            />
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default React.memo(WorkingTablesPanel)
